import React, { useContext, useEffect } from 'react';
import { Switch, Route, useHistory, Redirect } from 'react-router-dom';

import { AccountSettingsContext, TabNames, AccountSettingsState } from '../screens/context';

import { TabsContainer } from './Tabs';
import { Dashboard } from './Dashboard';
import { OrderHistory } from './OrderHistory';
import { Settings } from './SettingsTab/Settings';
import { CoordinatorDashboard } from './coordinator-dashboard/CoordinatorDashboard';

import { Routes } from 'routes';
import { useRootSelector } from 'common/features/featuresReducer';
import { useFeatureFlags, FeatureFlagTypes } from 'common/hooks/useFeatureFlags';

interface Tabs extends Pick<AccountSettingsState, 'activeTab' | 'setActiveTab' | 'pageIsLoading'> {
    isCoordinator: boolean | null;
}

const Tabs = ({ pageIsLoading, activeTab, setActiveTab, isCoordinator }: Tabs) => {
    const history = useHistory();
    const { isEnabled } = useFeatureFlags();

    const showCoordinatorDashboard =
        isCoordinator && !isEnabled(FeatureFlagTypes.enable_unified_coordinator_dashboard);

    useEffect(() => {
        if (isCoordinator !== null && activeTab === TabNames.CoordinatorDashboard) {
            if (!isCoordinator) {
                history.replace(Routes.ShopAccountSettingsDashboardTab);
            } else if (!showCoordinatorDashboard) {
                history.replace(Routes.CoordinatorDashboardHome);
            }
        }
    }, [showCoordinatorDashboard, activeTab, isCoordinator]);

    const setSelectedTab = (tab: string) => {
        setActiveTab(tab as TabNames);
        // do routing here
        switch (tab) {
            case TabNames.Dashboard: {
                history.replace(Routes.ShopAccountSettingsDashboardTab);
                return;
            }
            case TabNames.OrderHistory: {
                history.replace(Routes.ShopAccountSettingsOrderHistoryTab);
                return;
            }
            case TabNames.Settings: {
                history.replace(Routes.ShopAccountSettingsPersonalInformationTab);
                return;
            }
            case TabNames.CoordinatorDashboard: {
                history.replace(Routes.ShopAccountSettingsCoordinatorTab);
                return;
            }
            default:
                return;
        }
    };
    return (
        <div className="my-4">
            <TabsContainer
                isLoading={pageIsLoading}
                selectedTabName={activeTab}
                setSelectedTabName={setSelectedTab}
                tabItems={[
                    {
                        name: TabNames.Dashboard,
                        title: 'Personal Dashboard',
                    },
                    {
                        name: TabNames.OrderHistory,
                        title: 'Order History',
                        className: 'parti_dash_order_history',
                    },
                    {
                        name: TabNames.Settings,
                        title: 'Settings',
                    },
                    {
                        name: TabNames.CoordinatorDashboard,
                        title: 'Coordinator Dashboard',
                        className: `o:hidden ${showCoordinatorDashboard ? 'o:sm:block' : ''}`,
                    },
                ]}
            >
                {showCoordinatorDashboard && (
                    <div className="block sm:hidden text-center">
                        <TabsContainer
                            tabsListClasses=""
                            isLoading={pageIsLoading}
                            selectedTabName={activeTab}
                            setSelectedTabName={setSelectedTab}
                            tabItems={[
                                {
                                    name: TabNames.CoordinatorDashboard,
                                    title: 'Coordinator Dashboard',
                                },
                            ]}
                        />
                    </div>
                )}
                <Switch>
                    <Route path={Routes.ShopAccountSettings} exact>
                        <Redirect to={Routes.ShopAccountSettingsDashboardTab} />
                    </Route>
                    <Route exact path={Routes.ShopAccountSettingsTab}>
                        <Settings />
                    </Route>
                    <Route path={Routes.ShopAccountSettingsDashboardTab}>
                        <Dashboard />
                    </Route>
                    <Route path={Routes.ShopAccountSettingsOrderHistoryTab}>
                        <OrderHistory />
                    </Route>
                    {showCoordinatorDashboard && (
                        <Route path={Routes.ShopAccountSettingsCoordinatorTab}>
                            <CoordinatorDashboard />
                        </Route>
                    )}
                </Switch>
            </TabsContainer>
        </div>
    );
};

export const AccountSettingsContent = () => {
    const { pageIsLoading, activeTab, setActiveTab } = useContext(AccountSettingsContext);
    const isCoordinator = useRootSelector((s) => {
        const activeOrg = s.store.home.homeDetails?.defaultOrganization?.isActive;
        const loading = s.store.account.accountAsyncState.loading;

        if (activeOrg === undefined || loading) {
            return null;
        }

        return s.store.account.isCoordinator && activeOrg;
    });

    return (
        <div className="px-8 md:px-16 lg:px-32">
            <Tabs
                isCoordinator={isCoordinator}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                pageIsLoading={pageIsLoading}
            />
        </div>
    );
};
